import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Grid, Button, Paper, List, ListItem, ListItemIcon, ListItemText, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MemoryIcon from '@mui/icons-material/Memory';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import InsightsIcon from '@mui/icons-material/Insights';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import TerminalIcon from '@mui/icons-material/Terminal';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SEOHead from '../components/SEO/SEOHead';
import faqAiAutomationImage from '../assets/faq_ai_automation.jpg';
import medicalFlowImage from '../assets/medical_flow.png';
import aiCostRoiImage from '../assets/ai_cost_roi.png';
import aiTasksImage from '../assets/ai_tasks.png';
import retailDashboardImage from '../assets/retail_dashboard.png';
import softwareDevImage from '../assets/software_dev.png';

const AIAutomationIndia = () => {
    return (
        <Box sx={{ background: '#0A0E17', color: '#F5F7FA', minHeight: '100vh' }}>
            <SEOHead
                title="AI Automation Agency in India | Custom AI Agents & Workflows"
                description="VerveAI is a leading AI automation agency in India specializing in custom AI agents, LLM integrations, and business process automation for global clients."
                canonical="https://verveai.co/ai-automation-agency-india"
            />

            {/* Hero Section */}
            <Container maxWidth="lg" sx={{ pt: { xs: 12, md: 20 }, pb: 10 }}>
                <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            fontWeight: 800,
                            lineHeight: 1.1,
                            mb: 3,
                            background: 'linear-gradient(135deg, #F5F7FA 0%, #FF6B35 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Premier AI Automation Agency in India
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1.1rem', md: '1.35rem' },
                            color: 'rgba(245, 247, 250, 0.8)',
                            lineHeight: 1.6,
                            mb: 6,
                            maxWidth: '750px',
                            mx: 'auto',
                            fontWeight: 400
                        }}
                    >
                        We build intelligent, scalable AI workforces for forward-thinking businesses. Cut operational costs and 10x your efficiency with India's top AI engineering talent.
                    </Typography>

                    <Button
                        component={Link}
                        to="/contact"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            bgcolor: '#FF6B35',
                            color: '#0A0E17',
                            px: 4,
                            py: 1.75,
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 8px 24px rgba(255, 107, 53, 0.3)',
                            '&:hover': {
                                bgcolor: '#FF8555',
                                boxShadow: '0 12px 32px rgba(255, 107, 53, 0.4)',
                                transform: 'translateY(-2px)'
                            },
                        }}
                    >
                        Schedule a Consultation
                    </Button>
                </Box>
            </Container>

            {/* About Agency Section */}
            <Container maxWidth="lg" sx={{ pb: 12 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={10} sx={{ mx: 'auto' }}>
                        <Typography variant="h2" sx={{ textAlign: 'center', fontSize: '2.5rem', fontWeight: 700, mb: 6 }}>
                            Bridging the Gap Between <Box component="span" sx={{ color: '#FF6B35' }}>Strategy and Execution</Box>
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, bgcolor: 'rgba(20, 25, 35, 0.4)', p: 4, borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.7 }}>
                                <strong style={{ color: '#F5F7FA' }}>VerveAI</strong> is a premier AI automation agency in India focused on helping startups, SMEs, and modern businesses automate workflows, reduce operational overhead, and scale intelligently. We design and implement practical AI automation solutions that integrate seamlessly into existing systems—without unnecessary complexity.
                            </Typography>

                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.7 }}>
                                Our work spans <strong>business process automation, AI agents, LLM-powered workflows, intelligent document processing</strong>, and custom AI integrations tailored to real operational use cases. Unlike generic automation tools, VerveAI builds custom, outcome-driven AI systems aligned with business goals such as cost reduction, faster execution, and improved decision-making.
                            </Typography>

                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.7 }}>
                                As an engineering-first agency, we combine technical expertise, modern AI models, and cloud-native architecture to automate repetitive tasks across operations, customer support, finance, marketing, and internal workflows. Our solutions use proven technologies such as <strong>LLMs, APIs, cloud functions, vector databases</strong>, and automation pipelines, ensuring reliability and scalability from day one.
                            </Typography>

                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.7 }}>
                                VerveAI works closely with founders and teams to identify high-impact automation opportunities, rapidly prototype solutions, and deploy production-ready AI systems. Whether it’s automating internal workflows, building assistant bots, or implementing end-to-end business process automation, we focus on <strong>measurable outcomes—not experiments.</strong>
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Services Grid */}
            <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', py: 12 }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', fontSize: '2.5rem', fontWeight: 700, mb: 8 }}>
                        Our <Box component="span" sx={{ color: '#FF6B35' }}>Core Capabilities</Box>
                    </Typography>

                    <Grid container spacing={4}>
                        {[
                            {
                                icon: <SupportAgentIcon />,
                                title: "Custom AI Agents",
                                desc: "Autonomous agents that handle customer support, lead qualification, and complex scheduling without human intervention."
                            },
                            {
                                icon: <MemoryIcon />,
                                title: "LLM Integration",
                                desc: "Seamlessly integrate GPT-4, Claude, or open-source models into your existing software stack to add intelligence."
                            },
                            {
                                icon: <RocketLaunchIcon />,
                                title: "Workflow Automation",
                                desc: "End-to-end automation of manual business processes using smart triggers, APIs, and AI logic."
                            }
                        ].map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        bgcolor: 'rgba(20, 25, 35, 0.6)',
                                        border: '1px solid rgba(255, 107, 53, 0.1)',
                                        borderRadius: '16px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            borderColor: '#FF6B35',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                        }
                                    }}
                                >
                                    <Box sx={{ color: '#FF6B35', mb: 2, '& svg': { fontSize: 40 } }}>
                                        {service.icon}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#F5F7FA' }}>
                                        {service.title}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(245, 247, 250, 0.7)', lineHeight: 1.6 }}>
                                        {service.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Real-World Applications */}
            <Box sx={{ py: 12 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 700, mb: 4 }}>
                                Automate Your <Box component="span" sx={{ color: '#FF6B35' }}>Entire Business</Box>
                            </Typography>
                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', mb: 4, lineHeight: 1.7 }}>
                                We don't just build chatbots; we build intelligent operational layers. From instantaneous internal reporting to 24/7 customer handling, our systems work tirelessly so your team serves high-value tasks.
                            </Typography>

                            <List>
                                {[
                                    { title: "Intelligent Operations", desc: "Instant access to sales stats, order status, and customer queries via natural language dashboards." },
                                    { title: "Automated Outreach", desc: "Scale cold emails, lead qualification, and appointment setting without hiring more SDRs." },
                                    { title: "24/7 Support", desc: "Resolve L1/L2 queries, process payments, and upsell packages around the clock." }
                                ].map((item, i) => (
                                    <ListItem key={i} sx={{ px: 0, py: 2, alignItems: 'flex-start' }}>
                                        <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                                            <InsightsIcon sx={{ color: '#FF6B35' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="h6" sx={{ fontWeight: 600, color: '#F5F7FA' }}>{item.title}</Typography>}
                                            secondary={<Typography sx={{ color: 'rgba(245, 247, 250, 0.6)' }}>{item.desc}</Typography>}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(10, 14, 23, 0) 100%)',
                                borderRadius: '24px',
                                p: 1,
                                border: '1px solid rgba(255, 107, 53, 0.2)'
                            }}>
                                <Box sx={{ bgcolor: '#0A0E17', borderRadius: '20px', p: 4 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px', height: '100%' }}>
                                                <Typography variant="h4" sx={{ color: '#FF6B35', fontWeight: 700, mb: 1 }}>100+</Typography>
                                                <Typography variant="body2" sx={{ color: 'rgba(245,247,250,0.6)' }}>Daily Emails Automated</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px', height: '100%' }}>
                                                <Typography variant="h4" sx={{ color: '#FF6B35', fontWeight: 700, mb: 1 }}>24/7</Typography>
                                                <Typography variant="body2" sx={{ color: 'rgba(245,247,250,0.6)' }}>Uptime & Availability</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px' }}>
                                                <Typography variant="h6" sx={{ mb: 2 }}>System Status</Typography>
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00E676' }} />
                                                    <Typography variant="body2">Lead Generation Agent: <strong>Active</strong></Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#00E676' }} />
                                                    <Typography variant="body2">Support Bot: <strong>Handling 12 Chats</strong></Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Our Engagement Process */}
            <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', py: 12 }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', fontSize: '2.5rem', fontWeight: 700, mb: 8 }}>
                        How We <Box component="span" sx={{ color: '#FF6B35' }}>Work With You</Box>
                    </Typography>

                    <Grid container spacing={4}>
                        {[
                            {
                                icon: <AssessmentIcon />,
                                title: "1. Map & Audit",
                                desc: "We dive deep into your existing systems to identify bottlenecks. We don't guess—we analyze data to recommend high-impact solutions."
                            },
                            {
                                icon: <IntegrationInstructionsIcon />,
                                title: "2. Build & Integrate",
                                desc: "Our engineers implement the solution using custom coding, LLMs, and API connectors (Zapier/Make), ensuring perfect sync with your team."
                            },
                            {
                                icon: <ManageAccountsIcon />,
                                title: "3. Manage & Scale",
                                desc: "Automation isn't a one-time setup. We provide a managed journey, optimizing performance and adding capabilities as you grow."
                            }
                        ].map((step, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Box sx={{
                                    textAlign: 'center',
                                    p: 4,
                                    height: '100%',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        display: { xs: 'none', md: index !== 2 ? 'block' : 'none' },
                                        position: 'absolute',
                                        top: '50%',
                                        right: '-10%',
                                        width: '20%',
                                        height: '2px',
                                        bgcolor: 'rgba(255, 107, 53, 0.2)',
                                        zIndex: 0
                                    }
                                }}>
                                    <Box sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        bgcolor: 'rgba(255, 107, 53, 0.1)',
                                        color: '#FF6B35',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3,
                                        position: 'relative',
                                        zIndex: 1,
                                        border: '1px solid rgba(255, 107, 53, 0.2)'
                                    }}>
                                        {React.cloneElement(step.icon, { sx: { fontSize: 40 } })}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{step.title}</Typography>
                                    <Typography sx={{ color: 'rgba(245, 247, 250, 0.7)', lineHeight: 1.6 }}>{step.desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Case Study Highlight */}
            <Box sx={{ py: 12 }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        background: 'linear-gradient(180deg, rgba(20, 25, 35, 0.8) 0%, rgba(10, 14, 23, 1) 100%)',
                        border: '1px solid rgba(255, 107, 53, 0.2)',
                        borderRadius: '32px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }
                    }}>
                        <Box sx={{ p: { xs: 4, md: 8 }, flex: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                                <PrecisionManufacturingIcon sx={{ color: '#FF6B35' }} />
                                <Typography sx={{ color: '#FF6B35', fontWeight: 600, letterSpacing: '1px' }}>CASE STUDY HIGHLIGHT</Typography>
                            </Box>

                            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, fontWeight: 700, mb: 3 }}>
                                Transforming a Large Conglomerate's Operations
                            </Typography>

                            <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', mb: 4, lineHeight: 1.7 }}>
                                We developed a comprehensive digital ecosystem for a major Indian manufacturing client handling <strong>10,000+ daily queries</strong>.
                            </Typography>

                            <Grid container spacing={3} sx={{ mb: 6 }}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, borderLeft: '2px solid #FF6B35' }}>
                                        <Typography variant="h6" sx={{ color: '#F5F7FA', mb: 1 }}>$50M+ Process</Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(245, 247, 250, 0.6)' }}>Successfully digitized and automated</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 2, borderLeft: '2px solid #FF6B35' }}>
                                        <Typography variant="h6" sx={{ color: '#F5F7FA', mb: 1 }}>100+ Workforce</Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(245, 247, 250, 0.6)' }}>Empowered with custom Mobile Apps</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Button
                                component={Link}
                                to="/contact"
                                variant="contained"
                                sx={{
                                    bgcolor: '#FF6B35',
                                    color: '#0A0E17',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    '&:hover': { bgcolor: '#FF8555' }
                                }}
                            >
                                See How We Can Help You
                            </Button>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            bgcolor: 'rgba(255, 107, 53, 0.05)',
                            borderLeft: '1px solid rgba(255, 107, 53, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 4
                        }}>
                            {/* Abstract Visual Representation */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h1" sx={{ fontSize: '6rem', fontWeight: 900, color: 'rgba(255, 107, 53, 0.1)' }}>50X</Typography>
                                <Typography variant="h6" sx={{ color: '#FF6B35' }}>Efficiency Multiplier</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Why Choose Us */}
            <Container maxWidth="lg" sx={{ py: 12 }}>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, lineHeight: 1.2 }}>
                            World-Class Engineering, <br />
                            <Box component="span" sx={{ color: '#FF6B35' }}>Optimized for ROI</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '1.1rem', color: 'rgba(245, 247, 250, 0.8)', mb: 4, lineHeight: 1.7 }}>
                            As a specialized AI automation agency based in India, we deliver Silicon Valley-grade software at a competitive advantage. We focus purely on measurable business outcomes.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {['Deep technical expertise in GenAI', 'Agile development cycles (MVPs in weeks)', 'Cost-effective scalable solutions', 'Post-deployment support & optimization'].map((item) => (
                                <Box key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                    <CheckCircleOutlineIcon sx={{ color: '#00E676', fontSize: 20 }} />
                                    <Typography sx={{ color: 'rgba(245, 247, 250, 0.9)' }}>{item}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            p: 5,
                            borderRadius: '24px',
                            bgcolor: 'rgba(255, 107, 53, 0.05)',
                            border: '1px solid rgba(255, 107, 53, 0.2)',
                            position: 'relative'
                        }}>
                            <AutoFixHighIcon sx={{ fontSize: 60, color: '#FF6B35', mb: 3, opacity: 0.8 }} />
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                                Partner with the Best
                            </Typography>
                            <Typography sx={{ color: 'rgba(245, 247, 250, 0.7)', mb: 4 }}>
                                Don't let manual work hold you back. Let's build the intelligent systems that will power your next phase of growth.
                            </Typography>
                            <Button
                                component={Link}
                                to="/contact"
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    borderColor: '#FF6B35',
                                    color: '#FF6B35',
                                    '&:hover': {
                                        borderColor: '#FF8555',
                                        bgcolor: 'rgba(255, 107, 53, 0.1)'
                                    }
                                }}
                            >
                                Let's Talk
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* FAQ Section */}
            <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', py: 12 }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', fontSize: '2.5rem', fontWeight: 700, mb: 8 }}>
                        Frequently Asked Questions
                    </Typography>

                    <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
                        <Accordion
                            defaultExpanded
                            sx={{
                                bgcolor: 'rgba(20, 25, 35, 0.6)',
                                color: '#F5F7FA',
                                borderRadius: '16px !important',
                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                mb: 3,
                                '&:before': { display: 'none' },
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#FF6B35' }} />}
                                sx={{ px: 4, py: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>What is AI Automation Service?</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 4, pb: 4 }}>
                                <Grid container spacing={4} alignItems="center">
                                    <Grid item xs={12} md={7}>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8 }}>
                                            AI Automation Service is the strategic deployment of artificial intelligence to handle complex, repetitive business processes without human intervention. Unlike traditional automation that follows rigid rules, AI automation uses Machine Learning and Large Language Models (LLMs) to understand context, make decisions, and adapt to new information.
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8, mt: 2 }}>
                                            This means your systems can automatically draft emails, categorize support tickets, analyze sales data, and manage workflows 24/7. By bridging the gap between your existing software and intelligent decision-making, we turn manual bottlenecks into scalable, self-running operations, freeing your team to focus on high-value strategy and creative growth.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box
                                            component="img"
                                            src={faqAiAutomationImage}
                                            alt="AI Machine Learning and Creative Growth Strategy"
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '300px',
                                                borderRadius: '16px',
                                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                                objectFit: 'cover',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            sx={{
                                bgcolor: 'rgba(20, 25, 35, 0.6)',
                                color: '#F5F7FA',
                                borderRadius: '16px !important',
                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                mb: 3,
                                '&:before': { display: 'none' },
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#FF6B35' }} />}
                                sx={{ px: 4, py: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>How can AI Automation Service help my business?</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 4, pb: 4 }}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12}>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', mb: 4 }}>
                                            AI ensures efficiency, reduces human error, and works 24/7. Here is how we apply it across different sectors:
                                        </Typography>
                                    </Grid>

                                    {/* Medical */}
                                    <Grid item xs={12} md={4}>
                                        <Paper sx={{ p: 3, height: '100%', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, color: '#00E676' }}>
                                                <MedicalServicesIcon />
                                                <Typography variant="h6" sx={{ color: '#fff' }}>Medical</Typography>
                                            </Box>
                                            <Typography sx={{ color: 'rgba(245,247,250,0.7)', fontSize: '0.9rem', mb: 3 }}>
                                                Automate patient intake, appointment scheduling, and initial triage. AI agents can handle 24/7 patient queries and reduce administrative burden.
                                            </Typography>
                                            <Box
                                                component="img"
                                                src={medicalFlowImage}
                                                alt="Medical AI Automation"
                                                sx={{ width: '100%', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                                            />
                                        </Paper>
                                    </Grid>

                                    {/* Retail */}
                                    <Grid item xs={12} md={4}>
                                        <Paper sx={{ p: 3, height: '100%', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, color: '#FF6B35' }}>
                                                <LocalGroceryStoreIcon />
                                                <Typography variant="h6" sx={{ color: '#fff' }}>Retail</Typography>
                                            </Box>
                                            <Typography sx={{ color: 'rgba(245,247,250,0.7)', fontSize: '0.9rem', mb: 3 }}>
                                                Predict inventory needs, automate stock reordering, and provide hyper-personalized shopping experiences to increase sales.
                                            </Typography>
                                            <Box
                                                component="img"
                                                src={retailDashboardImage}
                                                alt="Retail AI Dashboard and Supply Chain"
                                                sx={{ width: '100%', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                                            />
                                        </Paper>
                                    </Grid>

                                    {/* Software Development */}
                                    <Grid item xs={12} md={4}>
                                        <Paper sx={{ p: 3, height: '100%', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, color: '#2979FF' }}>
                                                <TerminalIcon />
                                                <Typography variant="h6" sx={{ color: '#fff' }}>Software Dev</Typography>
                                            </Box>
                                            <Typography sx={{ color: 'rgba(245,247,250,0.7)', fontSize: '0.9rem', mb: 3 }}>
                                                Accelerate development cycles with AI coding assistants, automated QA testing agents, and intelligent bug triage systems to ship faster.
                                            </Typography>
                                            <Box
                                                component="img"
                                                src={softwareDevImage}
                                                alt="AI in Software Development"
                                                sx={{ width: '100%', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                                            />
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            sx={{
                                bgcolor: 'rgba(20, 25, 35, 0.6)',
                                color: '#F5F7FA',
                                borderRadius: '16px !important',
                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                mb: 3,
                                '&:before': { display: 'none' },
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#FF6B35' }} />}
                                sx={{ px: 4, py: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>How much does AI Automation Service cost?</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 4, pb: 4 }}>
                                <Grid container spacing={4} alignItems="center">
                                    <Grid item xs={12} md={7}>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8 }}>
                                            Even though the cost of AI Automation Service can vary greatly, we are confident that you will get positive ROI on the cost to build the solution - that the app will pay for itself by the cost savings it brings.
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8, mt: 2 }}>
                                            Simple solutions could be built in as little as <strong>$500 + usage costs</strong>, while complex solutions could take many more man hours. We assume an average rate of <strong>$40/hr for implementation</strong>, and <strong>$100/hr for consulting</strong> on structuring business processes to maximize automation. The exact mix of resources is determined by the complexity of your project.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box
                                            component="img"
                                            src={aiCostRoiImage}
                                            alt="AI Cost and ROI Analysis"
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '300px',
                                                borderRadius: '16px',
                                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                                objectFit: 'cover',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            sx={{
                                bgcolor: 'rgba(20, 25, 35, 0.6)',
                                color: '#F5F7FA',
                                borderRadius: '16px !important',
                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                mb: 3,
                                '&:before': { display: 'none' },
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#FF6B35' }} />}
                                sx={{ px: 4, py: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>What types of tasks can AI Automation Service handle?</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 4, pb: 4 }}>
                                <Grid container spacing={4} alignItems="center">
                                    <Grid item xs={12} md={7}>
                                        <Typography sx={{ color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8 }}>
                                            Our AI solutions are designed to handle complex, real-world business functions, not just simple chat interactions. We build systems that integrate deeply into your daily operations.
                                        </Typography>
                                        <Box component="ul" sx={{ pl: 2, mt: 2, color: 'rgba(245, 247, 250, 0.8)', lineHeight: 1.8 }}>
                                            <Box component="li" sx={{ mb: 1 }}><strong>Customer Support:</strong> 24/7 intelligent chatbots that resolve issues, process refunds, and escalate complex cases.</Box>
                                            <Box component="li" sx={{ mb: 1 }}><strong>Sales & Outreach:</strong> Automated cold emailing, lead qualification, and appointment setting agents.</Box>
                                            <Box component="li" sx={{ mb: 1 }}><strong>Document Processing:</strong> Extracting data from invoices, contracts, and receipts automatically.</Box>
                                            <Box component="li" sx={{ mb: 1 }}><strong>Internal Operations:</strong> HR onboarding, employee queries, and instant data retrieval dashboards.</Box>
                                            <Box component="li"><strong>Content & Marketing:</strong> Auto-generating social posts, blogs, and personalized marketing campaigns at scale.</Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box
                                            component="img"
                                            src={aiTasksImage}
                                            alt="AI Automating Real World Tasks"
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '300px',
                                                borderRadius: '16px',
                                                border: '1px solid rgba(255, 107, 53, 0.2)',
                                                objectFit: 'cover',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default AIAutomationIndia;
